// @ts-nocheck
import { IDdoc, PublishStatus } from '@/types';
import isEqual from 'fast-deep-equal';
import { Crypto } from '@peculiar/webcrypto';
import SafeBuffer from './sea-array';
import argon2 from 'argon2-browser';

import tweetnacl from 'tweetnacl';
import hkdf from 'futoin-hkdf';
import { toUint8Array, fromUint8Array } from 'js-base64';
import axios from 'axios';

import { CHAIN, UPLOAD_API_URL } from './constants';
import { ExtendedDoc, GenerateEncryptionMaterialParams } from './types';

const subtle = new Crypto().subtle;

const HKDF_KEY_INFO = 'encryptionKey';

// TODO: organize this file, has lots of duplicate code

export const parseDecryptedItem = (decryptedItem: any) => {
  try {
    return JSON.parse(decryptedItem);
  } catch (err) {
    return decryptedItem;
  }
};

export const canSkipPublish = (docItem: ExtendedDoc) => {
  if (docItem?.isDeleted || docItem?.markedForDeletion) return true;
  if (!docItem?.isLatestArchVersion) return false;

  const lastPublishStartTime = docItem.lastPublishStartTime;
  // return false if in progress and lastPublishStartTime is more than 30 seconds

  if (
    docItem.publishStatus === PublishStatus.InProgress &&
    lastPublishStartTime &&
    Date.now() - lastPublishStartTime > 30000
  )
    return false;

  const lastPublishedItems = docItem.lastPublishedContent || {};

  const isLastPublisedContentSame = isEqual(lastPublishedItems, {
    content: docItem.content,
    title: docItem.title,
  });

  return (
    docItem.publishStatus === PublishStatus.InProgress ||
    isLastPublisedContentSame
  );
};

export const toSha256 = async (data: string) => {
  const hash = await subtle.digest(
    {
      name: 'SHA-256',
    },
    new TextEncoder().encode(data)
  );
  return hash;
};

export const toAesKey = async (keyBytes: Buffer) => {
  const keyB64 = keyBytes.toString('base64');
  const k = keyB64.replace(/\+/g, '-').replace(/\//g, '_').replace(/\=/g, '');
  const jwk = { kty: 'oct', k: k, ext: false, alg: 'A256GCM' };
  const key = await subtle.importKey('jwk', jwk, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);
  return key;
};

export const seaDecrypt = async (ddocItem: string, eprivKey: string) => {
  const rawDocItem = JSON.parse(ddocItem.slice(3));

  const { ct, iv, s } = rawDocItem;
  const cipherTextBuffer = SafeBuffer.from(ct, 'base64');
  const ivBuffer = SafeBuffer.from(iv, 'base64');
  const saltBuffer = SafeBuffer.from(s, 'base64');
  const keyCombo = eprivKey + saltBuffer.toString('utf8');
  const keyComboBuffer = await toSha256(keyCombo);

  const hash = SafeBuffer.from(keyComboBuffer);
  const aesKey = await toAesKey(hash);

  const decrypted = await subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: new Uint8Array(ivBuffer),
      tagLength: 128,
    },
    aesKey,
    new Uint8Array(cipherTextBuffer)
  );

  const decryptedText = new TextDecoder('utf8').decode(decrypted);
  const parsedDecryptedItem = parseDecryptedItem(decryptedText);

  return parsedDecryptedItem;
};

export const isNullish = (value: any) => value === null || value === undefined;

export const ENCRYPTED_DDOC_KEYS: Array<Partial<keyof IDdoc>> = [
  'linkKey',
  'lastPublishedContent',
  'content',
  'linkKeyNonce',
  'commentKey',
];

export const decryptDdocItem = async (
  ddocItem: Partial<IDdoc>,
  eprivKey: string
) => {
  const data = {} as any;
  for (const key in ddocItem) {
    if (key === '_') continue;
    if (ENCRYPTED_DDOC_KEYS.includes(key as keyof IDdoc)) {
      data[key] = !isNullish(ddocItem[key as keyof IDdoc])
        ? await seaDecrypt(ddocItem[key], eprivKey)
        : null;
    } else {
      data[key] = ddocItem[key as keyof IDdoc];
    }
  }
  return data;
};

export const decryptSecretKey = async (
  docId: string,
  nonce: string,
  encryptedSecretKey: string
) => {
  const derivedKey = await deriveKeyFromAg2Hash(docId, toUint8Array(nonce));

  return tweetnacl.secretbox.open(
    toUint8Array(encryptedSecretKey),
    toUint8Array(nonce),
    derivedKey
  );
};

export const getDecrytedString = (
  encryptedString: string,
  nonce: string,
  secretKey: Uint8Array
) => {
  const decryptedValue = tweetnacl.secretbox.open(
    toUint8Array(encryptedString),
    toUint8Array(nonce),
    secretKey
  );

  if (decryptedValue) return new TextDecoder().decode(decryptedValue);

  return decryptedValue;
};

export const decryptTitleWithFileKey = async (
  encryptedTitle: string,
  fileKey: {
    key: string;
    iv: string;
    authTag: string;
  }
) => {
  const key = await subtle.importKey(
    'raw',
    toUint8Array(fileKey.key),
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );
  const iv = toUint8Array(fileKey.iv);

  const decryptedTitle = await subtle.decrypt(
    {
      name: 'AES-GCM',
      iv,
      tagLength: 128,
    },
    key,
    toUint8Array(encryptedTitle)
  );

  return new TextDecoder().decode(decryptedTitle);
};

enum KeyTypes {
  RSA = 'RSA',
  AES = 'AES',
}

const keyAlgoritms = {
  [KeyTypes.AES]: 'AES-GCM',
  [KeyTypes.RSA]: {
    name: 'RSA-OAEP',
    hash: 'SHA-256',
  },
};

const importKey = (
  pemContent: string,
  format: 'pkcs8' | 'raw' | 'spki',
  keyType: KeyTypes,
  keyUsage: KeyUsage[]
): Promise<CryptoKey> => {
  const uint8Content = toUint8Array(pemContent);
  return subtle.importKey(
    format,
    uint8Content.buffer,
    keyAlgoritms[keyType],
    false,
    keyUsage
  );
};

export const toRSAPrivateKey = async (pemContent: string) => {
  return await importKey(pemContent, 'pkcs8', KeyTypes.RSA, ['decrypt']);
};

export const rsaEncrypt = async (
  value: Record<any, any> | string,
  publicKey: string
): Promise<string> => {
  try {
    const encryptionKey = await importKey(publicKey, 'spki', KeyTypes.RSA, [
      'encrypt',
    ]);
    value = typeof value === 'string' ? value : JSON.stringify(value);
    const encoded = new TextEncoder().encode(value);
    const encrypted = await subtle.encrypt(
      {
        name: 'RSA-OAEP',
      },
      encryptionKey,
      encoded
    );

    return fromUint8Array(new Uint8Array(encrypted));
  } catch (error: any) {
    console.log(error);
    throw new Error(error?.message);
  }
};

export const rsaDecrypt = async (data: string, privateKey: string) => {
  const decryptionKey = await importKey(privateKey, 'pkcs8', KeyTypes.RSA, [
    'decrypt',
  ]);

  const decrypted = await subtle.decrypt(
    { name: 'RSA-OAEP' },
    decryptionKey,
    toUint8Array(data)
  );

  return new Uint8Array(decrypted);
};

const jsonToFile = (data: any, fileName: string) => {
  const blob = new Blob([JSON.stringify(data)], {
    type: 'application/json',
  });

  const file = new File([blob], fileName, {
    type: 'application/json',
  });

  return file;
};

export const uploadToIPFS = async (
  payload: File | Record<any, any>,
  name: string,
  contractAddress: string,
  invoker: string,
  token: string,
  fileTags?: string[]
) => {
  try {
    if (!token) throw new Error('Token is required');
    const body = new FormData();
    const file = payload instanceof File ? payload : jsonToFile(payload, name);
    body.append('file', file);
    body.append('name', name);

    const response = await axios.post(UPLOAD_API_URL, body, {
      headers: {
        Authorization: `Bearer ${token}`,
        contract: contractAddress,
        invoker: invoker,
        chain: CHAIN.id,
      },
      params: { tags: fileTags },
    });

    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage = error.response?.data?.message || error.message;
      throw new Error(errorMessage);
    } else {
      // Handle other types of errors
      throw new Error(`Upload failed: ${(error as Error).message}`);
    }
  }
};

const sequentialFetch = async (urls: string[]) => {
  for (const url of urls) {
    try {
      const response = await fetch(url);
      if (response.status === 200) {
        return response;
      }
    } catch (error) {
      console.error(`Failed to fetch from ${url}`, error);
    }
  }
};

export const getIPFSAssetSequential = async (ipfsHash: string) => {
  const gatewayURL = process.env.NEXT_PUBLIC_IPFS_GATEWAY_URLS?.split(', ');
  if (!gatewayURL || !gatewayURL.length) return;

  const response = await sequentialFetch(
    gatewayURL.map((url) => `${url}/ipfs/${ipfsHash}`)
  );

  if (!response) throw new Error(FAILED_IPFS_FETCH_ERROR);

  const data = await response.json();
  return { data };
};

export const generateEncryptionMaterial = async (
  opts: GenerateEncryptionMaterialParams
) => {
  if (!opts.ddocId) throw new Error('ddocId is required');
  if (opts.linkKeyNonce && opts.linkKey) {
    const { encryptedSecretKey, nonce, secretKey } =
      await getExistingEncryptionMaterial(
        opts.linkKey,
        opts.linkKeyNonce,
        opts.ddocId
      );
    if (secretKey) return { encryptedSecretKey, nonce, secretKey };
  }

  const { secretKey, nonce, encryptedSecretKey } = await getNaclSecretKey(
    opts.ddocId
  );

  return { hasLinkKeyChanged: true, secretKey, nonce, encryptedSecretKey };
};

export const getNaclSecretKey = async (ddocId: string) => {
  const { secretKey } = tweetnacl.box.keyPair();
  const nonce = tweetnacl.randomBytes(tweetnacl.secretbox.nonceLength);

  const derivedKey = await deriveKeyFromAg2Hash(ddocId, nonce);

  const encryptedSecretKey = fromUint8Array(
    tweetnacl.secretbox(secretKey, nonce, derivedKey),
    true
  );

  return { nonce, encryptedSecretKey, secretKey };
};

export const deriveKeyFromAg2Hash = async (pass: string, salt: Uint8Array) => {
  const key = await argon2.hash({
    pass,
    salt,
    type: argon2.ArgonType.Argon2id,
    hashLen: 32,
    time: 2,
    mem: 102400,
    parallelism: 8,
  });

  return hkdf(Buffer.from(key.hash), tweetnacl.secretbox.keyLength, {
    info: Buffer.from(HKDF_KEY_INFO),
  });
};

export const getExistingEncryptionMaterial = async (
  existingEncryptedSecretKey: string,
  existingNonce: string,
  docId: string
) => {
  const secretKey = await decryptSecretKey(
    docId,
    existingNonce,
    existingEncryptedSecretKey
  );
  return {
    encryptedSecretKey: existingEncryptedSecretKey,
    nonce: toUint8Array(existingNonce),
    secretKey,
  };
};
